import type { AppProps, AppInitialProps } from "next/app";
import Router from "next/router";
import { appWithTranslation } from "next-i18next";
import { useTranslation } from "next-i18next";
import { UTSLocale } from "@smile2impress/attraction-constants";
import RelaunchedBaseLayout from "layouts/relaunch/RelaunchLayout/RelaunchLayout";
import {
    getPlatformRelatedValue,
    PlatformRelatedProp,
} from "layouts/relaunch/RelaunchLayout/lib";
import { CmsPageNavigationProvider } from "context/cmsPageNavigationContext";
import { Popup } from "api/cms-website/cms-popups.types";
import { PromoCampaign } from "providers/CampaignProvider";

import { getAppMode } from "utils/getAppMode";
import { HreflangService } from "services/HreflangService";
import { UtmService } from "services/UtmService";
import { GoogleAdsService } from "services/GoogleAdsService";
import { VisitorTrajectoryService } from "services/VisitorTrajectoryService";
import { CanonicalService } from "services/CanonicalService";
import { AdClickIdService } from "services/AdClickIdService";
import { DrSmileService } from "services/DrSmileService";
import { RakutenService } from "services/RakutenService";
import { getUTSLocale } from "utils/locale";
import { HIDE_SOCIAL_WIDGET } from "constants/enum";
import { reportToSentry } from "utils/reportToSentry";

import { SmajlaService } from "../services/SmajlaService";

/**
 * Track client-side navigation failures to Sentry for investigation.
 * We want to check if these correlate with the "Invariant: attempted to hard
 * navigate to the same URL" error before attempting any fix.
 */
Router.events.on("routeChangeError", (err, url) => {
    if (err.cancelled) return;

    reportToSentry({
        error: `routeChangeError: ${err.message || JSON.stringify(err)}`,
        level: "warning",
        extra: { url, errorDetails: JSON.stringify(err) },
    });
});

const App = ({ Component, pageProps, router }: AppProps & AppInitialProps) => {
    const { isLiveMode, isPreliveMode } = getAppMode();
    const utsLocale = getUTSLocale(router.locale);

    const { i18n } = useTranslation("global");

    const {
        withHeader,
        withFixedHeader,
        withFooter,
        themeType,
        seoTitle,
        seoDescription,
        seo,
        hreflangAlternates,
        withBookButton,
        isErrorPage,
        withExpandableMobileMenu,
        withMessengerWidget = { mobile: false, desktop: true },
        hideMenu,
        hideFooterMenu,
        noFooterMarginTop,
        logoAsLink,
        isPreview,
        promoCampaigns,
        popups,
    } = pageProps;

    const resolvedMessengerWidget =
        getPlatformRelatedValue(withMessengerWidget);

    const showMessengerWidget = (locale: UTSLocale) => {
        return (
            ![UTSLocale.UKRAINE_UK, UTSLocale.US_EN].includes(locale) &&
            !HIDE_SOCIAL_WIDGET.includes(router.pathname)
        );
    };
    const showExpandableMessengerButton = (locale: UTSLocale) =>
        [
            UTSLocale.SPAIN_ES,
            UTSLocale.ITALY_IT,
            UTSLocale.GB_EN,
            UTSLocale.PORTUGAL_PT,
            UTSLocale.FRANCE_FR,
            UTSLocale.GERMANY_DE,
            UTSLocale.NL_NL,
        ].includes(locale);

    const canShowMessengerWidget = showMessengerWidget(utsLocale);

    return (
        <>
            <UtmService />
            <GoogleAdsService />
            <RakutenService />
            <DrSmileService />
            <SmajlaService />
            <VisitorTrajectoryService />
            <AdClickIdService />

            {/* GTM for tests on prelive */}
            {isPreliveMode && (
                <noscript
                    dangerouslySetInnerHTML={{
                        __html: `
                                <iframe
                                    src="https://www.googletagmanager.com/ns.html?id=GTM-MW5JM32&gtm_auth=E8PLDFZDuyxz3FKDaK59EA&gtm_preview=env-829&gtm_cookies_win=x"
                                    height="0"
                                    width="0"
                                    style="display:none;visibility:hidden">
                                </iframe>`,
                    }}
                />
            )}
            {isLiveMode && (
                <noscript
                    dangerouslySetInnerHTML={{
                        __html: `
                                <iframe
                                    src="https://www.googletagmanager.com/ns.html?id=GTM-MW5JM32"
                                    height="0"
                                    width="0"
                                    style="display:none;visibility:hidden">
                                </iframe>`,
                    }}
                />
            )}
            <HreflangService overrideAlternates={hreflangAlternates} />
            <CanonicalService overrideCanonicalUrl={seo?.canonicalURL} />

            <CmsPageNavigationProvider hreflangAlternates={hreflangAlternates}>
                <RelaunchedBaseLayout
                    themeType={themeType}
                    withHeader={withHeader}
                    withFixedHeader={withFixedHeader}
                    withFooter={withFooter}
                    seoTitle={seoTitle}
                    seoDescription={seoDescription}
                    seo={seo}
                    withBookButton={withBookButton}
                    hideMenu={hideMenu as PlatformRelatedProp}
                    hideFooterMenu={hideFooterMenu as PlatformRelatedProp}
                    logoAsLink={logoAsLink}
                    isErrorPage={isErrorPage}
                    withExpandableMobileMenu={withExpandableMobileMenu}
                    i18n={i18n}
                    noFooterMarginTop={noFooterMarginTop}
                    withMessengerWidget={{
                        mobile:
                            resolvedMessengerWidget.mobile &&
                            canShowMessengerWidget,
                        desktop:
                            resolvedMessengerWidget.desktop &&
                            canShowMessengerWidget,
                    }}
                    withExpandableMessengerButton={showExpandableMessengerButton(
                        utsLocale
                    )}
                    {...(isPreview &&
                        promoCampaigns && {
                            promoCampaigns: JSON.parse(
                                promoCampaigns
                            ) as unknown as PromoCampaign[],
                        })}
                    {...(isPreview &&
                        popups && {
                            popups: JSON.parse(popups) as unknown as Popup[],
                        })}
                >
                    <Component {...pageProps} />
                </RelaunchedBaseLayout>
            </CmsPageNavigationProvider>
        </>
    );
};

export default appWithTranslation(App);
